<template>
  <div class="nylon-Hero">
    <div class="nylon-Hero-liner">
      <div class="nylon-Hero-text">
        <h2>About Us</h2>
        <h3>Pittsburgh, Pennsylvania. Born and raised.</h3>
      </div>
    </div>
  </div>
  <div class="nylon-Content">
    <div class="poly-ContentWrapper u-pad--2">
      <div>
        <div id="general" class="nylon-Legalese">
          <div class="nylon-Text u-push--2">
            <h6 class="nylon-Text-sectionHeading nylon-Text-sectionHeading--grey">Who We Are</h6>
          </div>
          <div class="nylon-Text u-push--3 u-mobile-pad--2">
            <p>
              Cotton Bureau is kind of an odd company. To be honest, we’re not for everyone. We’re small, stubborn, and 100% independently owned and operated. We’re also, we hope, reasonable,
              pragmatic, and good at what we do. Those traits have served us — and thousands of designers and communities — well over the last 10+ years in business.
            </p>
            <p>
              Since starting in 2013, we’ve shipped over a million t-shirts, pins, stickers, posters, buttons, mugs, and more to pretty much every country on the planet, often with our own hands. We
              know what quality looks like and what it feels like. You might even say we’re grizzled veterans at this point. As long as we get to do good work with people we like though, we’re happy.
            </p>
            <p>
              If you think that sounds like the kind of company you want to work with, <a href="mailto:us@cottonbureau.com">get in touch</a>. We’d love to hear more about your story and how we can
              help.
            </p>
          </div>

          <div class="nylon-Text u-push--2">
            <h6 class="nylon-Text-sectionHeading nylon-Text-sectionHeading--grey">Where We Are</h6>
          </div>

          <div class="nylon-Text u-push--3 u-mobile-pad--2">
            <div class="small-photo-gallery u-push--2">
              <a :href="imageLink('office/pittsburgh.jpg')" target="_blank" rel="noopener">
                <imgix-image path="image" :imgix-params="{ path: 'office/pittsburgh.jpg' }" sizes="(min-width: 768px) 600px"></imgix-image>
              </a>
              <a :href="imageLink('office/inner-wall.jpg')" target="_blank" rel="noopener">
                <imgix-image path="image" :imgix-params="{ path: 'office/inner-wall.jpg' }" sizes="(min-width: 768px) 300px"></imgix-image>
              </a>
              <a :href="imageLink('office/outer-wall.jpg')" target="_blank" rel="noopener">
                <imgix-image path="image" :imgix-params="{ path: 'office/outer-wall.jpg' }" sizes="(min-width: 768px) 300px"></imgix-image>
              </a>
            </div>

            <p>
              Our current office is conveniently located right next to Google in Bakery Square and is an easy walk to an unhealthy amount of Slice on Broadway pizza and Jeni’s ice cream, which you can
              be sure we partake in regularly.
            </p>
          </div>

          <div class="nylon-Text u-push--2">
            <h6 class="nylon-Text-sectionHeading nylon-Text-sectionHeading--grey">History</h6>
          </div>

          <div class="nylon-Text u-push--3 u-mobile-pad--2">
            <div class="small-photo-gallery u-push--2">
              <a :href="imageLink('office/pink-wall-group.jpg')" target="_blank" rel="noopener">
                <imgix-image path="image" :imgix-params="{ path: 'office/pink-wall-group.jpg' }" sizes="(min-width: 768px) 600px"></imgix-image>
              </a>
              <a :href="imageLink('office/torrens-raw.jpg')" target="_blank" rel="noopener">
                <imgix-image path="image" :imgix-params="{ path: 'office/torrens-raw.jpg' }" sizes="(min-width: 768px) 300px"></imgix-image>
              </a>
              <a :href="imageLink('group-2.jpg')" target="_blank" rel="noopener">
                <imgix-image path="image" :imgix-params="{ path: 'group-2.jpg' }" sizes="(min-width: 768px) 300px"></imgix-image>
              </a>
            </div>

            <p>
              From meeting in coffee shops and libraries to our first office in the Strip, from working out of the corner of a print shop while shipping tens of thousands of Rogue NASA tees to
              designing and building our own custom 5,000 square foot space, from enduring a global pandemic to relocating to a beautiful co-working space… it’s been a roller coaster.
            </p>

            <p>
              We couldn’t have done any of this without the many people who have put their trust in us. Our goal has always been and will always remain taking the pain out of producing and fulfilling
              the highest quality merchandise for designers and communities of all kinds.
            </p>

            <p>Thank you to everyone who has been part of this journey with us, particularly Cotton Bureau alumni Sara, Caitlin, Briana, Web, Sam, Matt, Erin, Kyle, Michelle, and Jay.</p>
          </div>

          <div class="nylon-Text u-push--2">
            <h6 class="nylon-Text-sectionHeading nylon-Text-sectionHeading--grey">More About Us</h6>
          </div>

          <div class="nylon-Text u-push--3 u-mobile-pad--2">
            <p>
              If you’d like to read more about the history of Cotton Bureau, please feel free to <a href="https://blog.cottonbureau.com/archive/">browse the blog archives</a>. We’ve got hundreds of
              posts with designer interviews, company updates, and interesting links dating back to the beginning of Cotton Bureau.
            </p>

            <p>Quick facts:</p>
            <ul>
              <li>Founded in 2013 by Jay, Nate, and Matt.</li>
              <li><a href="https://indie.vc">Indie.vc</a> 2016 portfolio company.</li>
              <li>Tens of millions paid to designers.</li>
              <li>Hundreds of thousands of orders shipped.</li>
              <li>Eleven years in business and counting.</li>
              <li>Not a rocketship.</li>
            </ul>

            <p>Interested in writing about or interviewing us? Email <a href="mailto:us@cottonbureau.com">us@cottonbureau.com</a> to get in touch.</p>
            <p><a :href="brandGuideUrl">Download our brand guide</a>.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { buildUrl } from "@imgix/vue";
import StorefrontLayout from "@/layout/storefront-layout.vue";

import ImgixImage from "@/components/imgix-image.vue";

export default {
  name: "AboutUs",

  layout: StorefrontLayout,

  components: { ImgixImage },

  props: {
    brandGuideUrl: { type: String, required: true },
  },

  methods: {
    imageLink(path) {
      return buildUrl("image", { path });
    },
  },
};
</script>
